import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { inviteUsersToRoom } from '../../matrix/matrixFuntions.js';
import { X, Users, Search, Check, Send } from 'lucide-react';

export default function AddUsersToChat({ 
    show, 
    onClose, 
    roomId, 
    matrixClient, 
    loggedInUser, 
    practiceId, 
    token, 
    dispatch 
}) {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [inviting, setInviting] = useState(false);
    const [existingRoomMembers, setExistingRoomMembers] = useState({});
    const [loadingMembers, setLoadingMembers] = useState(false);

    // Get practice users from Redux (same pattern as ViewUsers)
    const practiceDetails = useSelector(state => state.practiceDetails);
    const practice = practiceDetails?.practice;
    const practiceUsers = practice ? practice.user : null;

    // Debug logs
    console.log("🔍 === AddUsersToChat DEBUG ===");
    console.log("🔍 show:", show);
    console.log("🔍 practiceDetails:", practiceDetails);
    console.log("🔍 practice:", practice);
    console.log("🔍 practiceUsers:", practiceUsers);
    console.log("🔍 loggedInUser:", loggedInUser);
    console.log("🔍 roomId:", roomId);
    console.log("🔍 matrixClient:", matrixClient ? "exists" : "null");
    console.log("🔍 existingRoomMembers:", existingRoomMembers);

    // Fetch existing room members when modal opens
    const fetchRoomMembers = useCallback(async () => {
        setLoadingMembers(true);
        try {
            console.log("🔍 Fetching existing room members for:", roomId);
            const roomMembersResponse = await matrixClient.getJoinedRoomMembers(roomId);
            console.log("🔍 Room members response:", roomMembersResponse);
            
            const joinedMembers = roomMembersResponse.joined || {};
            console.log("🔍 Current room members:", joinedMembers);
            
            // Convert object to array and add user info
            const membersList = Object.entries(joinedMembers).map(([userId, memberInfo]) => ({
                synapse_user_id: userId,
                display_name: memberInfo.display_name || userId,
                isCurrentUser: userId === loggedInUser?.synapse_user_id
            }));
            
            console.log("🔍 Processed members list:", membersList);
            setExistingRoomMembers(membersList);
        } catch (err) {
            console.error("❌ Error fetching room members:", err);
            dispatch({
                type: 'setErrorMsg',
                payload: 'Failed to fetch room members'
            });
        } finally {
            setLoadingMembers(false);
        }
    }, [roomId, matrixClient, loggedInUser?.synapse_user_id, dispatch]);

    React.useEffect(() => {
        if (show && roomId && matrixClient) {
            fetchRoomMembers();
        }
    }, [show, roomId, matrixClient, fetchRoomMembers]);

    // Filter and prepare users for display
    const eligibleUsers = React.useMemo(() => {
        console.log("🔍 Computing eligibleUsers...");
        console.log("🔍 practiceUsers:", practiceUsers);
        console.log("🔍 Array.isArray(practiceUsers):", Array.isArray(practiceUsers));
        console.log("🔍 existingRoomMembers:", existingRoomMembers);
        
        if (!practiceUsers || !Array.isArray(practiceUsers)) {
            console.log("🔍 No practice users or not an array, returning []");
            return [];
        }
        
        console.log("🔍 Filtering practice users...");
        const filtered = practiceUsers.filter(user => {
            console.log("🔍 Checking user:", user);
            const isCurrentUser = user._id === loggedInUser._id;
            const hasSynapseUserId = !!user.synapse_user_id;
            const isAlreadyInRoom = existingRoomMembers && existingRoomMembers.hasOwnProperty(user.synapse_user_id);
            
            console.log("🔍 User details:", {
                _id: user._id,
                firstname: user.firstname,
                lastname: user.lastname,
                username: user.username,
                isCurrentUser,
                hasSynapseUserId,
                isAlreadyInRoom,
                synapse_user_id: user.synapse_user_id
            });
            
            // Filter out current user, users without synapse_user_id, and users already in room
            const isEligible = !isCurrentUser && hasSynapseUserId && !isAlreadyInRoom;
            console.log("🔍 Is eligible:", isEligible, `(current: ${isCurrentUser}, hasMatrix: ${hasSynapseUserId}, inRoom: ${isAlreadyInRoom})`);
            return isEligible;
        });
        
        console.log("🔍 Eligible users after filtering:", filtered);
        return filtered;
    }, [practiceUsers, loggedInUser._id, existingRoomMembers]);


    // Filter users based on search term (using eligible users only)
    const filteredUsers = React.useMemo(() => {
        console.log("🔍 Computing filteredUsers...");
        console.log("🔍 eligibleUsers:", eligibleUsers);
        console.log("🔍 searchTerm:", searchTerm);
        
        if (!eligibleUsers.length) {
            console.log("🔍 No eligible users to search, returning []");
            return [];
        }
        
        // If no search term, return all eligible users
        if (!searchTerm.trim()) {
            console.log("🔍 No search term, returning all eligible users");
            return eligibleUsers;
        }
        
        const filtered = eligibleUsers.filter(user => {
            const firstName = user.firstname || '';
            const lastName = user.lastname || '';
            const username = user.username || '';
            
            const matchesFirst = firstName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesLast = lastName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesUsername = username.toLowerCase().includes(searchTerm.toLowerCase());
            
            console.log("🔍 Search match for user:", user.firstname + " " + user.lastname, {
                firstName,
                lastName,
                username,
                searchTerm,
                matchesFirst,
                matchesLast,
                matchesUsername,
                anyMatch: matchesFirst || matchesLast || matchesUsername
            });
            
            return matchesFirst || matchesLast || matchesUsername;
        });
        
        console.log("🔍 Filtered users after search:", filtered);
        return filtered;
    }, [eligibleUsers, searchTerm]);

    const toggleUserSelection = (user) => {
        setSelectedUsers(prev => {
            const isSelected = prev.some(selected => selected._id === user._id);
            if (isSelected) {
                return prev.filter(selected => selected._id !== user._id);
            } else {
                return [...prev, user];
            }
        });
    };

    const handleInviteUsers = async () => {
        if (selectedUsers.length === 0) {
            dispatch({
                type: 'setErrorMsg',
                payload: 'Please select at least one user to invite'
            });
            setTimeout(() => {
                dispatch({ type: 'setErrorMsg', payload: null });
            }, 3000);
            return;
        }

        // Check if any selected users don't have Matrix credentials
        const usersWithoutCredentials = selectedUsers.filter(user => 
            !user.synapse_user_id
        );

        if (usersWithoutCredentials.length > 0) {
            dispatch({
                type: 'setErrorMsg',
                payload: `${usersWithoutCredentials.length} user(s) don't have chat access. Only users with Matrix credentials can be invited.`
            });
            setTimeout(() => {
                dispatch({ type: 'setErrorMsg', payload: null });
            }, 3000);
            return;
        }

        setInviting(true);
        try {
            console.log("🔍 Inviting users to room:", roomId);
            console.log("🔍 Selected users:", selectedUsers);

            const inviteResults = await inviteUsersToRoom(
                roomId, 
                selectedUsers, 
                matrixClient, 
                dispatch
            );

            if (inviteResults.success) {
                console.log("✅ Users invited successfully");
                dispatch({
                    type: 'setSuccessMsg',
                    payload: `Successfully invited ${inviteResults.invitedCount} user(s) to the chat`
                });
                setTimeout(() => {
                    dispatch({ type: 'setSuccessMsg', payload: null });
                }, 3000);
                
                // Close modal and reset selection
                setSelectedUsers([]);
                onClose();
            } else {
                console.error("❌ Some invitations failed:", inviteResults.errors);
                dispatch({
                    type: 'setErrorMsg',
                    payload: `Failed to invite some users: ${inviteResults.errors.join(', ')}`
                });
                setTimeout(() => {
                    dispatch({ type: 'setErrorMsg', payload: null });
                }, 3000);
            }
        } catch (error) {
            console.error("❌ Error inviting users:", error);
            dispatch({
                type: 'setErrorMsg',
                payload: 'Failed to invite users. Please try again.'
            });
            setTimeout(() => {
                dispatch({ type: 'setErrorMsg', payload: null });
            }, 3000);
        } finally {
            setInviting(false);
        }
    };

    if (!show) return null;

    console.log("🔍 === RENDER DEBUG ===");
    console.log("🔍 practiceUsers:", practiceUsers);
    console.log("🔍 eligibleUsers:", eligibleUsers);
    console.log("🔍 eligibleUsers.length:", eligibleUsers.length);
    console.log("🔍 filteredUsers:", filteredUsers);
    console.log("🔍 filteredUsers.length:", filteredUsers.length);
    console.log("🔍 searchTerm:", searchTerm);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-teal-700" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            Chat Room Members
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loadingMembers ? (
                        <div className="text-center py-8 text-gray-500">
                            Loading room members...
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Current Room Members Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                    Current Members ({Object.keys(existingRoomMembers).length})
                                </h4>
                                {Object.keys(existingRoomMembers).length === 0 ? (
                                    <div className="text-center py-4 text-gray-500 text-sm">
                                        No members in this room yet
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {Object.entries(existingRoomMembers).map(([userId, memberInfo]) => {
                                            const isCurrentUser = userId === loggedInUser?.synapse_user_id;
                                            return (
                                                <div
                                                    key={userId}
                                                    className={`flex items-center justify-between p-3 rounded-lg border ${
                                                        isCurrentUser 
                                                            ? 'bg-teal-50 border-teal-200' 
                                                            : 'bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                            isCurrentUser
                                                                ? 'bg-teal-700 text-white'
                                                                : 'bg-gray-300 text-gray-700'
                                                        }`}>
                                                            {(memberInfo.display_name || userId).charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {memberInfo.display_name || userId}
                                                                {isCurrentUser && (
                                                                    <span className="ml-2 text-xs text-teal-700 font-medium">
                                                                        (You)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {isCurrentUser && (
                                                        <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-1 rounded">
                                                            Current User
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Eligible Users Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                    Add Members ({eligibleUsers.length})
                                </h4>
                                <div className="mb-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            placeholder="Search users to add..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                                {eligibleUsers.length === 0 ? (
                                    <div className="text-center py-4 text-gray-500 text-sm">
                                        {searchTerm 
                                            ? `No users found matching "${searchTerm}"`
                                            : "No eligible users to add"
                                        }
                                    </div>
                                ) : filteredUsers.length === 0 && searchTerm ? (
                                    <div className="text-center py-4 text-gray-500 text-sm">
                                        No users found matching "{searchTerm}"
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {filteredUsers.map((user) => {
                                            const isSelected = selectedUsers.some(selected => selected._id === user._id);
                                            const hasMatrixCredentials = user.synapse_user_id;
                                            return (
                                                <div
                                                    key={user._id}
                                                    onClick={() => toggleUserSelection(user)}
                                                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 ${
                                                        isSelected ? 'bg-teal-50 border-teal-700' : 'border-gray-200'
                                                    } ${!hasMatrixCredentials ? 'opacity-60' : ''}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                            hasMatrixCredentials
                                                                ? 'bg-blue-500 text-white'
                                                                : 'bg-gray-300 text-gray-700'
                                                        }`}>
                                                            {(user.firstname || user.username || 'U').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {user.firstname} {user.lastname}
                                                                {!hasMatrixCredentials && (
                                                                    <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                                                                        No Chat Access
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {user.username}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                        isSelected 
                                                            ? 'bg-teal-700 border-teal-700' 
                                                            : 'border-gray-300'
                                                    }`}>
                                                        {isSelected && <Check className="w-3 h-3 text-white" />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600">
                            {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected to invite
                        </span>
                        {selectedUsers.length > 0 && (
                            <button
                                onClick={() => setSelectedUsers([])}
                                className="text-sm text-teal-700 hover:text-teal-800 font-medium"
                            >
                                Clear Selection
                            </button>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleInviteUsers}
                            disabled={selectedUsers.length === 0 || inviting}
                            className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                        >
                            {inviting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Inviting...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Invite {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
